import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import { setErrors } from '../common/errorsSlice'
import {
  ReportMember,
  MemberCondition,
  MonthlySearchCondition,
  MemberReportCategoryWork,
  MemberReportApplicationProportion,
  ApplicationWorkTime,
  ApplicationTimeLine,
  MemberReportMixChart,
  MemberReportHeatMap,
} from '../../types/member'

import {
  UserData,
  DataExists,
} from '../../types/report'

import {
  getReportMember,
  getMonthlyReportExists,
  getMonthlyUserData,
  getMonthlyCategoryWork,
  getMonthlyApplicationProportion,
  getMonthlyApplicationWorkTime,
  getMonthlyTimeLine,
  getMonthlyMixChart,
  getMonthlyHeatMap,
} from '../../apis/memberReport'

interface MemberReportState {
  isMemberLoading: boolean
  isDataExistsLoading: boolean
  isUserDataLoading: boolean
  isCategoryWorkLoading: boolean
  isApplicationProportion: boolean
  isApplicationWorkTime: boolean
  isApplicationTimeLine: boolean
  isMixChartLoading: boolean
  isHeatMapLoading: boolean
  member: ReportMember
  isDisApprove: boolean | undefined
  evaluationExists: boolean
  userProcessExists: boolean
  userData: UserData[]
  categoryWork: MemberReportCategoryWork
  applicationProportion: MemberReportApplicationProportion
  applicationWorkTime: ApplicationWorkTime[]
  applicationTimeLine: ApplicationTimeLine[]
  mixChart: MemberReportMixChart
  heatMap: MemberReportHeatMap
}

const initialState: Partial<MemberReportState> = {
  evaluationExists: undefined,
  userProcessExists: undefined,
  isDisApprove: undefined,
  isMemberLoading: true,
  isDataExistsLoading: true,
  isUserDataLoading: true,
  isCategoryWorkLoading: true,
  isApplicationProportion: true,
  isApplicationWorkTime: true,
  isApplicationTimeLine: true,
  isMixChartLoading: true,
  isHeatMapLoading: true,
}

const monthlyMemberReportSlice = createSlice({
  name: 'monthlyMemberReport',
  initialState,
  reducers: {
    requestMemberStart: (state) => {
      state.isMemberLoading = true
      return state
    },
    requestDataExistsStart: (state) => {
      state.isDataExistsLoading = true
      return state
    },
    requestUserDataStart: (state) => {
      state.isUserDataLoading = true
      return state
    },
    requestCategoryWork: (state) => {
      state.isCategoryWorkLoading = true
      return state
    },
    requestApplicationProportion: (state) => {
      state.isApplicationProportion = true
      return state
    },
    requestApplicationWorkTime: (state) => {
      state.isApplicationWorkTime = true
      return state
    },
    requestApplicationTimeLine: (state) => {
      state.isApplicationTimeLine = true
      return state
    },
    requestMixChart: (state) => {
      state.isMixChartLoading = true
      return state
    },
    requestHeatMap: (state) => {
      state.isHeatMapLoading = true
    },
    buildMemberSuccess: (state, action: PayloadAction<ReportMember>) => {
      state.isMemberLoading = false
      state.member = action.payload
      return state
    },
    buildMemberReportExists: (state, action: PayloadAction<DataExists>) => {
      state.isDataExistsLoading = false
      state.evaluationExists = action.payload.evaluation_exists
      state.userProcessExists = action.payload.user_process_exists
      return state
    },
    buildMemberReportUserDataSuccess: (state, action: PayloadAction<UserData[]>) => {
      state.isUserDataLoading = false
      state.userData = action.payload
      return state
    },
    buildMemberReportCategoryWork: (state, action: PayloadAction<MemberReportCategoryWork>) => {
      state.isCategoryWorkLoading = false
      state.categoryWork = action.payload
      return state
    },
    buildMemberReportApplicationProportion: (state, action: PayloadAction<MemberReportApplicationProportion>) => {
      state.isApplicationProportion = false
      state.applicationProportion = action.payload
      return state
    },
    buildMemberReportApplicationWorkTime: (state, action: PayloadAction<ApplicationWorkTime[]>) => {
      state.isApplicationWorkTime = false
      state.applicationWorkTime = action.payload
      return state
    },
    buildMemberReportApplicationTimeline: (state, action: PayloadAction<ApplicationTimeLine[]>) => {
      state.isApplicationTimeLine = false
      state.applicationTimeLine = action.payload
      return state
    },
    buildMemberReportMixChart: (state, action: PayloadAction<MemberReportMixChart>) => {
      state.isMixChartLoading = false
      state.mixChart = action.payload
      return state
    },
    buildMemberReportHeatMap: (state, action: PayloadAction<MemberReportHeatMap>) => {
      state.isHeatMapLoading = false
      state.heatMap = action.payload
      return state
    },
    setMemberReportApproval: (state) => {
      state.isDataExistsLoading = false
      state.isDisApprove = false
      return state
    },
    setMemberReportDisApproval: (state) => {
      state.isDataExistsLoading = false
      state.evaluationExists = false
      state.userProcessExists = false
      state.isDisApprove = true
      return state
    },
    resetState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestMemberStart,
  requestDataExistsStart,
  requestUserDataStart,
  requestCategoryWork,
  requestApplicationProportion,
  requestApplicationWorkTime,
  requestApplicationTimeLine,
  requestMixChart,
  requestHeatMap,
  buildMemberSuccess,
  buildMemberReportExists,
  buildMemberReportUserDataSuccess,
  buildMemberReportCategoryWork,
  buildMemberReportApplicationProportion,
  buildMemberReportApplicationWorkTime,
  buildMemberReportApplicationTimeline,
  buildMemberReportMixChart,
  buildMemberReportHeatMap,
  setMemberReportApproval,
  setMemberReportDisApproval,
  resetState,
} = monthlyMemberReportSlice.actions

export const monthlyMemberReportReducer = monthlyMemberReportSlice.reducer

export const fetchMemberReportExists = (
  params: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestDataExistsStart())
  getMonthlyReportExists(params)
    .then((result) => {
      dispatch(setMemberReportApproval())
      dispatch(buildMemberReportExists(result))
    })
    .catch((error) => {
      switch (error.status) {
        case 403:
          dispatch(setMemberReportDisApproval())
          break
        case 409:
          dispatch(setMemberReportDisApproval())
          break
        default:
          dispatch(setErrors(error))
          break
      }
    })
}

export const fetchReportUserData = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestUserDataStart())
  getMonthlyUserData(param)
    .then((result: UserData[]) => {
      dispatch(buildMemberReportUserDataSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportCategoryWork = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestCategoryWork())
  getMonthlyCategoryWork(param)
    .then((result: MemberReportCategoryWork) => {
      dispatch(buildMemberReportCategoryWork(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationProportion = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestApplicationProportion())
  getMonthlyApplicationProportion(param)
    .then((result: MemberReportApplicationProportion) => {
      dispatch(buildMemberReportApplicationProportion(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationWorkTime = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestApplicationWorkTime())
  getMonthlyApplicationWorkTime(param)
    .then((result: ApplicationWorkTime[]) => {
      dispatch(buildMemberReportApplicationWorkTime(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationTimeLine = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestApplicationTimeLine())
  getMonthlyTimeLine(param)
    .then((result: ApplicationTimeLine[]) => {
      dispatch(buildMemberReportApplicationTimeline(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchMixChart = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestMixChart())
  getMonthlyMixChart(param)
    .then((result: MemberReportMixChart) => {
      dispatch(buildMemberReportMixChart(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchHeatMap = (
  param: MonthlySearchCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestHeatMap())
  getMonthlyHeatMap(param)
    .then((result: MemberReportHeatMap) => {
      dispatch(buildMemberReportHeatMap(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportMember = (
  param: MemberCondition,
): AppThunk => async (dispatch) => {
  dispatch(requestMemberStart())
  getReportMember(param)
    .then((result: ReportMember) => {
      dispatch(buildMemberSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}
